import { DataQuality, MeterReadingTypeEnum, ProvidersEnum, SiteType } from "./enum";
import { meterDataComponents, meterDataPaths, NonReadonly, ReadonlyOrNot } from "./index";

export type IKind<T> = {
    kind: T;
};

export namespace MeterDataDto {
    export type SyncResponse<T> = {
        data: Array<T>;
        next_cursor: string | null;
        has_more: boolean;
    };

    export type Tenant = {
        tenantId: string;
        name?: string;
    };

    export type TenantProvider = {
        tenantId: string;
        providerId: ProvidersEnum;
        enabled?: boolean | null;
    };

    export enum GoogleBigQueryMeterType {
        IN = 1,
        OUT = 2,
    }

    export enum GoogleBigQueryGranularity {
        HOUR = "hour",
        QUARTER = "quarter",
    }

    export enum GoogleBigQueryEnergyField {
        V = "v",
        KW = "kw",
    }

    export type NoMeterExtraData = NonReadonly<meterDataComponents["schemas"]["NoMeterExtraData"]>;
    export type BigQueryExtraData = NonReadonly<
        meterDataComponents["schemas"]["BigQueryExtraData"]
    >;
    export type CSVExtraData = NonReadonly<meterDataComponents["schemas"]["CSVExtraData"]>;
    export type ElexonExtraData = NonReadonly<meterDataComponents["schemas"]["ElexonExtraData"]>;
    export type WattsonExtraData = NonReadonly<meterDataComponents["schemas"]["WattsonExtraData"]>;
    export type DatadisExtraData = NonReadonly<meterDataComponents["schemas"]["DatadisExtraData"]>;
    export type CesExtraData = NonReadonly<meterDataComponents["schemas"]["CesExtraData"]>;
    export type EnecoExtraData = NonReadonly<meterDataComponents["schemas"]["EnecoExtraData"]>;
    export type EddieExtraData = NonReadonly<meterDataComponents["schemas"]["EddieExtraData"]>;
    export type MidAmericanExtraData = NonReadonly<
        meterDataComponents["schemas"]["MidAmericanExtraData"]
    >;
    export type TenaskaPowerServiceExtraData = NonReadonly<
        meterDataComponents["schemas"]["TenaskaPowerServiceExtraData"]
    >;
    export type ApiUploadExtraData = NonReadonly<
        meterDataComponents["schemas"]["ApiUploadExtraData"]
    >;
    export type SemoMeterExtraData = NonReadonly<
        meterDataComponents["schemas"]["SemoMeterExtraData"]
    >;
    export type ModityMeterExtraData = NonReadonly<
        meterDataComponents["schemas"]["ModityMeterExtraData"]
    >;
    export type UtilityApiExtraData = NonReadonly<
        meterDataComponents["schemas"]["UtilityApiExtraData"]
    >;
    export type NWCPUDMeterExtraData = NonReadonly<
        meterDataComponents["schemas"]["NWCPUDMeterExtraData"]
    >;
    export type ZepakMeterExtraData = NonReadonly<
        meterDataComponents["schemas"]["ZepakMeterExtraData"]
    >;
    export type EnelNaMeterExtraData = NonReadonly<
        meterDataComponents["schemas"]["EnelNaMeterExtraData"]
    >;
    export type HistoricalMeteredToMonthlyGridExtra = NonReadonly<
        meterDataComponents["schemas"]["HistoricalMeteredToMonthlyGridExtra"]
    >;
    export type BrookfieldRenewablesExtraData = NonReadonly<
        meterDataComponents["schemas"]["BrookfieldRenewablesExtraData"]
    >;
    export type CenPrtmeExtraData = NonReadonly<
        meterDataComponents["schemas"]["CenPrtmeExtraData"]
    >;
    export type StatkraftExtraData = NonReadonly<
        meterDataComponents["schemas"]["StatkraftExtraData"]
    >;
    export type SocoXtrExtraData = NonReadonly<meterDataComponents["schemas"]["SocoXtrExtraData"]>;

    export type DukeEnergyExtraData = NonReadonly<
        meterDataComponents["schemas"]["DukeEnergyExtraData"]
    >;

    export type EngieNAExtraData = NonReadonly<meterDataComponents["schemas"]["EngieNAExtraData"]>;

    export type FingridSoapExtraData = NonReadonly<
        meterDataComponents["schemas"]["FingridSoapExtraData"]
    >;

    export type MeterExtraData = NonReadonly<meterDataComponents["schemas"]["MeterExtraData"]>;

    export type Meter = NonReadonly<meterDataComponents["schemas"]["Meter"]>;

    export type MeterOption = meterDataComponents["schemas"]["MeterOption"];

    /** Meter without extra data */
    export type BasicMeter = meterDataComponents["schemas"]["BasicMeter"];

    export type MeterCreation = meterDataComponents["schemas"]["MeterCreation"];

    export type PutSiteMetersBySiteIdPayload = {
        meterIds: Array<number>;
    };

    export type SiteMeterReading = {
        startTimeUtc: Date; // utc
        endTimeUtc: Date; //utc
        startTimeLocal: string;
        endTimeLocal: string;
        siteId: string;
        type: MeterReadingTypeEnum;
        quantityWh: number | null;
        validatedWh: number | null;
        nonValidatedWh: number | null;
        providerEstimatedWh: number | null;
        internalEstimatedWh: number | null;
        //status: string;
        created: Date;
        updated: Date;
        warnings?: Array<string> | null;
        childWarnings?: Array<string> | null;
        outlier: boolean;
        quality: DataQuality;
        version: number;

        //deleted: Date;
    };

    export type MeterDataSyncResponse = SyncResponse<SiteMeterReading>;

    export type IntervalMeter = meterDataComponents["schemas"]["IntervalMeter"];

    export type GetIntervalMetersResponse =
        meterDataComponents["schemas"]["GetIntervalMetersResponse"];

    export type GetIntervalMetersFiltersResponse =
        meterDataComponents["schemas"]["GetIntervalMetersFiltersResponse"];

    export type GetIntervalMetersPayload =
        meterDataPaths["/{tenantId}/interval-meters"]["get"]["parameters"]["query"] &
            meterDataPaths["/{tenantId}/interval-meters"]["get"]["parameters"]["path"];

    export type GetIntervalMetersFiltersPayload =
        meterDataPaths["/{tenantId}/interval-meters-filters"]["get"]["parameters"]["path"];

    export type MonthlyGridMeter = meterDataComponents["schemas"]["MonthlyGridMeter"];

    export type GetMonthlyGridMetersResponse =
        meterDataComponents["schemas"]["GetMonthlyGridMetersResponse"];

    export type GetMonthlyGridMetersFiltersResponse =
        meterDataComponents["schemas"]["GetMonthlyGridMetersFiltersResponse"];

    export type GetMonthlyGridMetersPayload =
        meterDataPaths["/{tenantId}/monthly-grid-meters"]["get"]["parameters"]["query"] &
            meterDataPaths["/{tenantId}/monthly-grid-meters"]["get"]["parameters"]["path"];

    export type GetMonthlyGridMetersFiltersPayload =
        meterDataPaths["/{tenantId}/monthly-grid-meters-filters"]["get"]["parameters"]["path"];

    export type GetMonthlyGridMetersCodesWithSitesPayload =
        meterDataPaths["/{tenantId}/202505/monthly-grid-consumption"]["get"]["parameters"]["path"];

    export type GetMonthlyGridMetersCodesWithSitesResponse =
        meterDataComponents["schemas"]["GetMonthlyGridMetersWithSitesResponse"];

    export type GetMeteredMetersWithSitesPayload =
        meterDataPaths["/{tenantId}/202505/sync/meters"]["get"]["parameters"]["path"] &
            meterDataPaths["/{tenantId}/202505/sync/meters"]["get"]["parameters"]["query"];

    export type GetMeteredMetersWithSitesResponse =
        meterDataComponents["schemas"]["MeteredMetersWithSitesResponse"];

    export type GetMeterOptionsPayload =
        meterDataPaths["/{tenantId}/meter-options"]["get"]["parameters"]["path"] &
            meterDataPaths["/{tenantId}/meter-options"]["get"]["parameters"]["query"];

    export type GetWarningTypeOptionsPayload =
        meterDataPaths["/{tenantId}/warning-type-options"]["get"]["parameters"]["path"] &
            meterDataPaths["/{tenantId}/warning-type-options"]["get"]["parameters"]["query"];

    export type GetMonitoringSiteDetailsKpisByTenantIdPayload =
        meterDataPaths["/{tenantId}/monitoring-site-details-kpis"]["get"]["parameters"]["path"] &
            meterDataPaths["/{tenantId}/monitoring-site-details-kpis"]["get"]["parameters"]["query"];

    export type GetAggregatedWarningsPerSitePayload =
        meterDataPaths["/{tenantId}/aggregated-warnings-per-site"]["get"]["parameters"]["path"] &
            meterDataPaths["/{tenantId}/aggregated-warnings-per-site"]["get"]["parameters"]["query"];

    export type MonitoringSiteDetailsKpis =
        meterDataPaths["/{tenantId}/monitoring-site-details-kpis"]["get"]["responses"]["200"]["content"]["application/json"];

    export type SiteLevelWarning = meterDataComponents["schemas"]["SiteLevelWarning"];
    export type MeterLevelWarningsBreakdown =
        meterDataComponents["schemas"]["MeterLevelWarningsBreakdown"];
    export type SiteLevelWarningsBreakdown =
        meterDataComponents["schemas"]["SiteLevelWarningsBreakdown"];

    export type Credential = {
        name: string;
        description?: string | null;
        secret: string | Record<string, never>;
        expirationDate?: Date;
    };

    export type TotalConsumptionAndProductionKpi = {
        totalConsumptionWh: number | null;
        totalProductionWh: number | null;
    };

    export type ActiveSitesKpi = {
        totalActiveSites: number;
        activeConsumptionSites: number;
        activeProductionSites: number;
    };

    export type RegionOption = {
        regionId: string;
        regionName: string;
    };

    export type BiddingZoneOption = {
        biddingZoneId: string;
        biddingZoneName: string;
    };

    export type SiteOption = {
        siteId: string;
        siteName: string;
        regionName: string;
        biddingZoneName: string;
        timezone: string;
    };

    export type ProviderOption = {
        providerId: string;
        providerName: string;
    };

    export type MeterDataMeterBreakdownRow = {
        meterCode: string;
        consumptionWh: number | null;
        productionWh: number | null;
        numReadings: number;
        expectedNumReadings: number;
        dataCompleteness: number;
        dataProvider: string;
        lastDataUpdate: Date | null;
        dateOfFirstReading: Date | null;
        dateOfLastReading: Date | null;
        currentDelayDays: number | null;
        expectedDelayDays: number | null;
        initialTimeToCheckLocal: Date;
        endTimeToCheckLocal: Date | null;
    };

    export type MeterDataSiteBreakdownRow = {
        siteName: string;
        siteType: SiteType;
        energySource: string | null;
        region: string;
        biddingZone: string;
        timeZone: string;
        consumptionWh: number | null;
        productionWh: number | null;
        numReadings: number;
        expectedNumReadings: number;
        dataCompleteness: number;
        dataProvider: string;
        lastDataUpdate: Date | null;
        dateOfFirstReading: Date | null;
        dateOfLastReading: Date | null;
        currentDelayDays: number | null;
        expectedDelayDays: number | null;
        dataPerMeter?: Array<MeterDataMeterBreakdownRow> | null;
    };

    export type MeterDataSiteBreakdown = {
        rowCount: number;
        rows: Array<MeterDataSiteBreakdownRow>;
    };

    export type HourlyAggregatedMeterData = {
        startTime: Date;
        consumptionWh: number | null;
        productionWh: number | null;
    };

    export type DataCompleteness = NonReadonly<meterDataComponents["schemas"]["DataCompleteness"]>;

    export type AggregatedWarningsPerSite =
        meterDataComponents["schemas"]["AggregatedWarningsPerSite"];

    export type GetHourlyMeterWarningsPayload =
        meterDataPaths["/{tenantId}/hourly-meter-warnings"]["get"]["parameters"]["path"] &
            meterDataPaths["/{tenantId}/hourly-meter-warnings"]["get"]["parameters"]["query"];
    export type SiteHourlyMeterWarnings = meterDataComponents["schemas"]["SiteHourlyMeterWarnings"];

    export type GetHourlySiteWarningsPayload =
        meterDataPaths["/{tenantId}/hourly-site-warnings"]["get"]["parameters"]["path"] &
            meterDataPaths["/{tenantId}/hourly-site-warnings"]["get"]["parameters"]["query"];
    export type SiteHourlySiteWarnings = meterDataComponents["schemas"]["SiteHourlySiteWarnings"];

    export type MetersDataReadingsStatistic =
        meterDataComponents["schemas"]["MeterDataReadingStatistic"];
    export type MetersDataUpdatesStatistic =
        meterDataComponents["schemas"]["MeterDataUpdatesStatistic"];

    export type ConsumptionBySite = {
        page: Array<{
            siteId: string;
            siteName: string;
            countryName: string;
            consumptionWh: number;
        }>;
        totalSites: number;
    };

    export type File = {
        fileId: string;
        tenantId: string;
        userId?: string;
        executionId?: string;
        created: Date;
    };

    export type GridConsumptionFilterByRegionsPayload =
        meterDataPaths["/{tenantId}/invoiced-grid-consumption/filters/active-regions"]["get"]["parameters"]["path"] &
            meterDataPaths["/{tenantId}/invoiced-grid-consumption/filters/active-regions"]["get"]["parameters"]["query"];
    export type GridConsumptionFilterByRegion = Array<
        meterDataComponents["schemas"]["RegionOption"]
    >;

    export type GridConsumptionFilterBySitesPayload =
        meterDataPaths["/{tenantId}/invoiced-grid-consumption/filters/active-sites"]["get"]["parameters"]["path"] &
            meterDataPaths["/{tenantId}/invoiced-grid-consumption/filters/active-sites"]["get"]["parameters"]["query"];
    export type SiteBrief = meterDataComponents["schemas"]["SiteBrief"];

    export type GridConsumptionMonthlyBreakdownPayload =
        meterDataPaths["/{tenantId}/invoiced-grid-consumption/{dataSubType}/monthly-breakdown"]["get"]["parameters"]["path"] &
            meterDataPaths["/{tenantId}/invoiced-grid-consumption/{dataSubType}/monthly-breakdown"]["get"]["parameters"]["query"];

    export type GridConsumptionMonthlyBreakdownItem = NonReadonly<
        meterDataComponents["schemas"]["GridConsumptionMonthlyBreakdownItem"]
    >;

    export type GridConsumptionMonthlyBreakdown = Array<GridConsumptionMonthlyBreakdownItem>;

    export type GridConsumptionKpisPayload =
        meterDataPaths["/{tenantId}/invoiced-grid-consumption/{dataSubType}/kpis"]["get"]["parameters"]["path"] &
            meterDataPaths["/{tenantId}/invoiced-grid-consumption/{dataSubType}/kpis"]["get"]["parameters"]["query"];

    export type GridConsumptionKpis = meterDataComponents["schemas"]["GridConsumptionKpi"];

    // Alerts
    export type Alert = NonReadonly<meterDataComponents["schemas"]["Alert"]>;
    export type AlertComment = NonReadonly<meterDataComponents["schemas"]["AlertComment"]>;

    // GET alerts
    export type GetAlertsResponse = NonReadonly<
        meterDataComponents["schemas"]["GetAlertsResponse"]
    >;
    export type GetAlertsPayload = ReadonlyOrNot<
        meterDataPaths["/{tenantId}/alerts"]["get"]["parameters"]["path"] &
            meterDataPaths["/{tenantId}/alerts"]["get"]["parameters"]["query"]
    >;
    export type AlertSortingFields = NonReadonly<
        meterDataComponents["schemas"]["AlertSortingFields"]
    >;

    // GET alert
    export type GetAlertByAlertIdPayload = ReadonlyOrNot<
        meterDataPaths["/{tenantId}/alerts/{alertId}"]["get"]["parameters"]["path"]
    >;

    // GET alerts-kpis
    export type GetAlertsKpisResponse = NonReadonly<
        meterDataComponents["schemas"]["GetAlertsKpisResponse"]
    >;
    export type GetAlertsKpisPayload = ReadonlyOrNot<
        meterDataPaths["/{tenantId}/alerts-kpis"]["get"]["parameters"]["path"] &
            meterDataPaths["/{tenantId}/alerts-kpis"]["get"]["parameters"]["query"]
    >;

    // GET regions-with-alerts
    export type GetRegionsWithAlertsPayload = ReadonlyOrNot<
        meterDataPaths["/{tenantId}/regions-with-alerts"]["get"]["parameters"]["path"] &
            meterDataPaths["/{tenantId}/regions-with-alerts"]["get"]["parameters"]["query"]
    >;

    // GET sites-with-alerts
    export type GetSitesWithAlertsPayload = ReadonlyOrNot<
        meterDataPaths["/{tenantId}/sites-with-alerts"]["get"]["parameters"]["path"] &
            meterDataPaths["/{tenantId}/sites-with-alerts"]["get"]["parameters"]["query"]
    >;

    // GET comments
    export type GetAlertCommentsPayload = ReadonlyOrNot<
        meterDataPaths["/{tenantId}/alerts/{alertId}/comments"]["get"]["parameters"]["path"]
    >;

    // POST comment
    export type PostAlertCommentPayload = ReadonlyOrNot<
        meterDataPaths["/{tenantId}/alerts/{alertId}/comments"]["post"]["parameters"]["path"] &
            meterDataPaths["/{tenantId}/alerts/{alertId}/comments"]["post"]["requestBody"]["content"]["application/json"]
    >;

    // POST alert status
    export type PostAlertStatusPayload = ReadonlyOrNot<
        meterDataPaths["/{tenantId}/alerts/{alertId}/status"]["post"]["parameters"]["path"] &
            meterDataPaths["/{tenantId}/alerts/{alertId}/status"]["post"]["requestBody"]["content"]["application/json"]
    >;
}

// TODO change it
export namespace ProviderDto {
    type BaseRateLimit = {
        group?: string;
    };

    export type RateLimitEnum =
        | "RateLimitOnSeconds"
        | "RateLimitOnMinutes"
        | "RateLimitOnHours"
        | "RateLimitOnDays"
        | "NoRateLimit";

    export type RateLimitOnSeconds = {
        seconds: number;
        limit: number;
    } & IKind<"RateLimitOnSeconds"> &
        BaseRateLimit;

    export type RateLimitOnMinutes = {
        minutes: number;
        limit: number;
    } & IKind<"RateLimitOnMinutes"> &
        BaseRateLimit;
    export type RateLimitOnHours = {
        hours: number;
        limit: number;
    } & IKind<"RateLimitOnHours"> &
        BaseRateLimit;
    export type RateLimitOnDays = {
        days: number;
        limit: number;
    } & IKind<"RateLimitOnDays"> &
        BaseRateLimit;
    export type NoRateLimit = IKind<"NoRateLimit"> & BaseRateLimit;

    export type RateLimit =
        | NoRateLimit
        | RateLimitOnSeconds
        | RateLimitOnMinutes
        | RateLimitOnHours
        | RateLimitOnDays;

    type CredentialsLevel = "Flexidao" | "TenantProvider" | "Meter" | "NoCredentials";

    export type Provider = {
        providerId: ProvidersEnum;
        recheckAvailable: boolean;
        meterDiscovery: boolean;
        credentialLevel: Array<CredentialsLevel>;
        rateLimits: Array<RateLimit>;
    };
}
